import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaTh, FaList } from 'react-icons/fa';
import ShopsMainBox from "./ShopsMainBox";
import ShopTable from "./ShopTable";
import ButtonBar from "../../Elements/ButtonBar";
import BulkActionsBar from "../../Elements/BulkActionsBar";
import ShopsModalAdv from "./ShopsModalAdv";
import { UserContext } from "../../../context/UserContext";

export default function Shops() {
  const [token, , , venueID, , , , , , changeVenue, refreshVenues] = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [shops, setShops] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [activeModal, setActiveModal] = useState(false);
  const [id, setId] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [selectedShopIds, setSelectedShopIds] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const processedBillingReturnRef = useRef("");

  const menu_bar = [
    {
      title: 'Tiendas',
      buttons: [
        {
          label: 'Crear Tienda',
          color: 'green',
          onClick: () => {
            setId(null); // Asegurar que no hay ID al crear nueva tienda
            setActiveModal(true);
          },
        },
      ],
      viewToggle: (
        <div className="flex gap-1">
          <button
            onClick={() => setViewMode('cards')}
            className={`p-2 rounded transition-all ${
              viewMode === 'cards'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            title="Vista de tarjetas"
          >
            <FaTh />
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded transition-all ${
              viewMode === 'table'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            title="Vista de tabla"
          >
            <FaList />
          </button>
        </div>
      ),
    },
  ];
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const showActivationBanner = searchParams.get("activation") === "required";
  const billingState = searchParams.get("billing");
  const billingVenueId = searchParams.get("venue_id");
  const billingSessionId = searchParams.get("session_id");
  const billingReturnKey = `${billingState || ""}:${billingVenueId || ""}:${billingSessionId || ""}:${location.pathname}:${location.search}`;

  const fetchVenues = useCallback(async (isRetry = false) => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    };
    const response = await fetch(`/v1/venues/all`, requestOptions);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Error al cargar tiendas' }));
      return { ok: false, error: errorData.detail || 'Error al cargar las tiendas' };
    }
    const data = await response.json();
    return { ok: true, data: Array.isArray(data) ? data : [] };
  }, [token]);

  const getShops = useCallback(async () => {
    if (!token) {
      setErrorMessage('No hay token de autenticación');
      setLoaded(true);
      return null;
    }

    setLoaded(false);
    setErrorMessage('');

    const doFetch = async (retryCount = 0) => {
      try {
        const result = await fetchVenues();
        if (result.ok) {
          setShops(result.data);
          if (venueID) {
            const currentVenue = result.data.find((venue) => String(venue.id) === String(venueID));
            if (currentVenue) {
              changeVenue(
                currentVenue.id,
                currentVenue.name,
                currentVenue.image_path,
                currentVenue.venue_type,
                currentVenue.activation_status,
                currentVenue.subscription_status,
                currentVenue.trial_ends_at
              );
              if (
                currentVenue.activation_status === "active" &&
                showActivationBanner
              ) {
                navigate("/my_shops", { replace: true });
              }
            }
          }
          setErrorMessage('');
          setLoaded(true);
          return result.data;
        }
        // First failure: retry once after a short delay (handles auth/race on first load)
        if (retryCount === 0) {
          await new Promise((r) => setTimeout(r, 600));
          return doFetch(1);
        }
        setErrorMessage(result.error);
        setLoaded(true);
        return null;
      } catch (error) {
        console.error("Error fetching shops:", error);
        if (retryCount === 0) {
          await new Promise((r) => setTimeout(r, 600));
          return doFetch(1);
        }
        setErrorMessage("Error de conexión al cargar tiendas");
        setLoaded(true);
        return null;
      }
    };

    return doFetch();
  }, [token, fetchVenues, venueID, changeVenue, navigate, showActivationBanner]);

  const handleUpdate = async (id) => {
    setId(id);
    setActiveModal(true);
  };

  const handleDelete = async (id) => {
    // Confirmar antes de eliminar
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta tienda?')) {
      return;
    }
    
    try {
      const requestOptions = {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      };
      const response = await fetch(`/v1/venues/${id}`, requestOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Error al eliminar tienda' }));
        setErrorMessage(errorData.detail || "Error al eliminar tienda");
        return;
      }
      
      // Recargar lista de tiendas
      const updatedShops = await getShops();
      if (refreshVenues) {
        await refreshVenues();
      }

      // Si se eliminó la tienda actualmente seleccionada, mover selección a otra
      if (String(id) === String(venueID)) {
        const nextVenue = Array.isArray(updatedShops) ? updatedShops[0] : null;
        if (nextVenue) {
          changeVenue(
            nextVenue.id,
            nextVenue.name,
            nextVenue.image_path || "",
            nextVenue.venue_type,
            nextVenue.activation_status,
            nextVenue.subscription_status,
            nextVenue.trial_ends_at
          );
        } else {
          changeVenue("", "", "", "", "pending", "");
        }
      }
      setErrorMessage(""); // Limpiar errores si fue exitoso
    } catch (error) {
      console.error("Error deleting shop:", error);
      setErrorMessage("Error de conexión al eliminar tienda");
    }
  };

  useEffect(() => {
    if (!Array.isArray(shops)) return;
    setSelectedShopIds((prev) => prev.filter((id) => shops.some((shop) => String(shop.id) === String(id))));
  }, [shops]);

  const toggleSelectedShop = (id) => {
    setSelectedShopIds((prev) =>
      prev.some((currentId) => String(currentId) === String(id))
        ? prev.filter((currentId) => String(currentId) !== String(id))
        : [...prev, id]
    );
  };

  const clearSelectedShops = () => setSelectedShopIds([]);

  const selectVisibleShops = () => setSelectedShopIds((shops || []).map((shop) => shop.id));

  const deleteShopById = async (id) => {
    const response = await fetch(`/v1/venues/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Error al eliminar tienda' }));
      throw new Error(errorData.detail || 'Error al eliminar tienda');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedShopIds.length) return;
    if (!window.confirm(`¿Eliminar ${selectedShopIds.length} tiendas seleccionadas?`)) return;

    setIsBulkDeleting(true);
    const results = await Promise.allSettled(selectedShopIds.map(deleteShopById));
    const failedCount = results.filter((result) => result.status === 'rejected').length;
    const updatedShops = await getShops();
    if (refreshVenues) {
      await refreshVenues();
    }
    setIsBulkDeleting(false);
    setSelectedShopIds([]);

    // Si la tienda seleccionada se eliminó en el bulk, elegir otra
    const deletedSelectedVenue = selectedShopIds.some((deletedId) => String(deletedId) === String(venueID));
    if (deletedSelectedVenue) {
      const nextVenue = Array.isArray(updatedShops) ? updatedShops[0] : null;
      if (nextVenue) {
        changeVenue(
          nextVenue.id,
          nextVenue.name,
          nextVenue.image_path || "",
          nextVenue.venue_type,
          nextVenue.activation_status,
          nextVenue.subscription_status,
          nextVenue.trial_ends_at
        );
      } else {
        changeVenue("", "", "", "", "pending", "");
      }
    }

    if (failedCount > 0) {
      setErrorMessage(`Se eliminaron ${selectedShopIds.length - failedCount} tiendas y ${failedCount} fallaron.`);
      return;
    }

    setErrorMessage("");
  };

  const handleManageBilling = async (venueId) => {
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ venue_id: venueId }),
      };
      const response = await fetch("/v1/billing/portal", requestOptions);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const detail = data?.detail || "No se pudo abrir el portal de facturación.";
        setErrorMessage(detail);
        if (detail.includes("No Stripe customer found")) {
          window.alert(
            "Esta tienda todavía no tiene una suscripción activa. Primero debes iniciar un plan para que Stripe cree el cliente y habilite la facturación."
          );
        }
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setErrorMessage("Stripe no devolvió una URL del portal.");
      }
    } catch (error) {
      console.error("Error opening billing portal:", error);
      setErrorMessage("Error de conexión al abrir la facturación");
    }
  };

  const handleCancelSubscription = async (venueId) => {
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ venue_id: venueId, flow_type: "subscription_cancel" }),
      };
      const response = await fetch("/v1/billing/portal", requestOptions);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setErrorMessage(data?.detail || "No se pudo abrir la cancelación de la suscripción.");
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setErrorMessage("Stripe no devolvió una URL de cancelación.");
      }
    } catch (error) {
      console.error("Error opening subscription cancellation flow:", error);
      setErrorMessage("Error de conexión al abrir la cancelación de suscripción");
    }
  };

  const handleStartBilling = async (venueId, planTier = "basico") => {
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ venue_id: venueId, plan_id: planTier || "basico" }),
      };
      const response = await fetch("/v1/billing/checkout", requestOptions);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setErrorMessage(data?.detail || "No se pudo iniciar el checkout del plan.");
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setErrorMessage("Stripe no devolvió una URL de checkout.");
      }
    } catch (error) {
      console.error("Error starting billing checkout:", error);
      setErrorMessage("Error de conexión al iniciar la facturación");
    }
  };

  const handleStartSetup = async (venueId, planTier = "basico") => {
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ venue_id: venueId, plan_id: planTier || "basico" }),
      };
      const response = await fetch("/v1/billing/setup-checkout", requestOptions);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setErrorMessage(data?.detail || "No se pudo iniciar el pago de alta.");
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setErrorMessage("Stripe no devolvió una URL para el pago de alta.");
      }
    } catch (error) {
      console.error("Error starting setup checkout:", error);
      setErrorMessage("Error de conexión al iniciar el pago de alta");
    }
  };

  const handleModal = () => {
    if (activeModal) {
      // Si se está cerrando el modal, recargar datos y limpiar ID
      setActiveModal(false);
      setId(null);
      // Pequeño delay para asegurar que el modal se cierre antes de recargar
      setTimeout(() => {
        getShops();
      }, 100);
    } else {
      // Si se está abriendo el modal
      setActiveModal(true);
    }
  };

  useEffect(() => {
    if (token) {
      getShops();
    }
  }, [token, getShops]);

  useEffect(() => {
    if (!token || !billingState || !refreshVenues) return;
    if (processedBillingReturnRef.current === billingReturnKey) return;

    let cancelled = false;
    processedBillingReturnRef.current = billingReturnKey;

    const syncBillingState = async () => {
      const syncHeaders = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      };

      const trySyncVenue = async (venueId, sessionId = null) => {
        try {
          await fetch("/v1/billing/sync", {
            method: "POST",
            headers: syncHeaders,
            body: JSON.stringify({
              venue_id: venueId,
              session_id: sessionId || undefined,
            }),
          });
        } catch (error) {
          console.error("Error syncing billing state:", error);
        }
      };

      if (billingVenueId) {
        await trySyncVenue(billingVenueId, billingSessionId);
      }

      await refreshVenues();
      const latestShopsResult = await fetchVenues();
      if (latestShopsResult.ok) {
        setShops(latestShopsResult.data);
      }

      // Fallback for localhost/dev or portal returns where webhook delivery may lag or be absent.
      const shopsToInspect = latestShopsResult.ok ? latestShopsResult.data : [];
      if (!billingVenueId && Array.isArray(shopsToInspect)) {
        for (const shop of shopsToInspect) {
          if (shop?.stripe_customer_id && (shop.activation_status !== "active" || !shop.subscription_status)) {
            await trySyncVenue(shop.id);
          }
        }
        await refreshVenues();
        const refreshedShopsResult = await fetchVenues();
        if (refreshedShopsResult.ok) {
          setShops(refreshedShopsResult.data);
        }
      }

      if (!cancelled) {
        navigate("/my_shops", { replace: true });
      }
    };

    syncBillingState();

    return () => {
      cancelled = true;
    };
  }, [token, billingState, billingVenueId, billingSessionId, billingReturnKey, refreshVenues, fetchVenues, navigate]);

  return (
    <div className='flex flex-col gap-4'>
      {showActivationBanner && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Completa primero el pago de alta de la tienda para activar los modulos operativos. Mientras tanto, aqui puedes editar los datos basicos y continuar con Stripe.
        </div>
      )}
      {/* Desktop: barra normal */}
      <div className="hidden md:block">
        {menu_bar.map((menuItem, index) => (
          <ButtonBar
            key={index}
            title={menuItem.title}
            buttons={menuItem.buttons}
            viewToggle={menuItem.viewToggle}
          />
        ))}
      </div>
      {/* Móvil: botón alargado como Socios/Personal/Superusuarios */}
      <div className="block md:hidden bg-white p-4 rounded-lg shadow border border-gray-200 mb-2 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-gray-800">
            Tiendas
          </h2>
          {/* Mantener el toggle de vista accesible en móvil */}
          {menu_bar[0]?.viewToggle && (
            <div className="flex items-center">
              {menu_bar[0].viewToggle}
            </div>
          )}
        </div>
        <button
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
          onClick={() => {
            setId(null);
            setActiveModal(true);
          }}
        >
          Crear Tienda
        </button>
      </div>
      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{errorMessage}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setErrorMessage("")}
          >
            <span className="text-red-700">×</span>
          </button>
        </div>
      )}

      <BulkActionsBar
        selectedCount={selectedShopIds.length}
        onClear={clearSelectedShops}
        onSelectAll={selectVisibleShops}
        onDelete={handleBulkDelete}
        deleteLabel="Eliminar tiendas"
        disabled={isBulkDeleting}
      />
      
      <ShopsModalAdv 
          active={activeModal}
          handleModal={handleModal}
          token={token}
          id={id}
          setErrorMessage={setErrorMessage}
      />
      {loaded && (
        <div className="w-full">
          {viewMode === 'cards' ? (
            <ShopsMainBox
              cards={shops}
              handleUpdate={handleUpdate}
              handleDelete={handleDelete}
              selectedIds={selectedShopIds}
              onToggleSelect={toggleSelectedShop}
              handleManageBilling={handleManageBilling}
              handleCancelSubscription={handleCancelSubscription}
              handleStartBilling={handleStartBilling}
              handleStartSetup={handleStartSetup}
            />
          ) : (
            <ShopTable
              shops={shops}
              loading={false}
              onEdit={handleUpdate}
              onDelete={handleDelete}
              selectedIds={selectedShopIds}
              onToggleSelect={toggleSelectedShop}
              onManageBilling={handleManageBilling}
              onCancelSubscription={handleCancelSubscription}
              onStartBilling={handleStartBilling}
              onStartSetup={handleStartSetup}
            />
          )}
        </div>
      )}
      {!loaded && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-500">Cargando tiendas...</p>
          </div>
        </div>
      )}
    </div>
  );
}
